/**
 * Symphony GROUP CHAT, Phase 10b — web's calls for WhatsApp's message actions (`POST /api/group-chat/<action>`), and
 * what each answer does to the shared group store. The decisions are pure, in `lib/group-message-actions.ts`; this
 * file only moves data. Nothing here names the caller: the body names the group and the message.
 */
import type { Message } from './use-chat';
import {
  groupChatAction,
  groupMessageFromWire,
  handleLostAccess,
  replaceGroupMessage,
  requestGroupReload,
  setGroupHidden,
  setGroupPins,
  setGroupStar,
  showGroupNotice,
} from './group-chat-api';
import {
  forwardSummary,
  messageActionErrorKey,
  pinResult,
  withMyReaction,
  withRowReactions,
  type ForwardSummary,
  type PinResult,
} from './group-message-actions';

/** The answered row (with the conversation it belongs to), or null. */
function answeredRow(raw: unknown, conversationId: string): Message | null {
  const row = groupMessageFromWire(raw);
  return row ? { ...row, conversationId: row.conversationId ?? conversationId } : null;
}

const serverId = (m: Message) => m.finalId ?? m.id ?? '';

/**
 * React (`emoji` = "" clears): optimistic on the row, replaced by the answer's row; a refusal or a failure puts the
 * row back as it was, with its message.
 */
export async function reactToGroupMessage(conversationId: string, row: Message, viewerId: string, emoji: string): Promise<boolean> {
  const messageId = serverId(row);
  const before: Message = { ...row, id: messageId, conversationId };
  replaceGroupMessage(withRowReactions(before, withMyReaction(row.groupMeta?.reactions, viewerId, emoji)));
  const outcome = await groupChatAction('react', { conversationId, messageId, emoji });
  const answered = outcome.kind === 'ok' ? answeredRow(outcome.data.message, conversationId) : null;
  if (answered) {
    replaceGroupMessage(answered);
    return true;
  }
  replaceGroupMessage(before);
  if (outcome.kind === 'refused' && handleLostAccess(conversationId, outcome.refusal)) return false;
  showGroupNotice(messageActionErrorKey('react', outcome.kind === 'ok' ? { kind: 'failed', status: 200 } : outcome));
  return false;
}

/** Save an edit. Null on success (the row is replaced), else the message the composer shows. */
export async function editGroupMessage(conversationId: string, messageId: string, text: string): Promise<string | null> {
  const outcome = await groupChatAction('edit-message', { conversationId, messageId, text });
  const answered = outcome.kind === 'ok' ? answeredRow(outcome.data.message, conversationId) : null;
  if (answered) {
    replaceGroupMessage(answered);
    return null;
  }
  if (outcome.kind === 'refused' && handleLostAccess(conversationId, outcome.refusal)) return null;
  return messageActionErrorKey('edit', outcome.kind === 'ok' ? { kind: 'failed', status: 200 } : outcome);
}

/** Pin / unpin. The answer's pins replace the banner's list (on PIN_LIMIT_REACHED too); the caller asks to replace. */
export async function pinGroupMessage(conversationId: string, messageId: string, pinned: boolean, replaceOldest = false): Promise<PinResult> {
  const outcome = await groupChatAction('pin', { conversationId, messageId, pinned, ...(replaceOldest ? { replaceOldest: true } : {}) });
  if (outcome.kind === 'refused' && handleLostAccess(conversationId, outcome.refusal)) return { kind: 'error', pins: null, messageKey: '' };
  const result = pinResult(outcome, pinned, replaceOldest);
  if (result.pins) setGroupPins(conversationId, result.pins);
  if (result.kind === 'done') showGroupNotice(result.toastKey);
  else if (result.kind === 'error') showGroupNotice(result.messageKey);
  return result;
}

/** Star / unstar: optimistic, put back on a refusal (SAVED_LIMIT_REACHED says so) or a failure. */
export async function starGroupMessage(conversationId: string, messageId: string, starred: boolean): Promise<boolean> {
  setGroupStar(conversationId, messageId, starred);
  const outcome = await groupChatAction('star', { conversationId, messageId, starred });
  if (outcome.kind === 'ok') {
    setGroupStar(conversationId, messageId, outcome.data.starred === true);
    return true;
  }
  setGroupStar(conversationId, messageId, !starred);
  if (outcome.kind === 'refused' && handleLostAccess(conversationId, outcome.refusal)) return false;
  showGroupNotice(messageActionErrorKey('star', outcome));
  return false;
}

/** Unstar from the Starred messages screen (no group store to touch). True when it is no longer starred. */
export async function unstarFromList(conversationId: string, messageId: string): Promise<boolean> {
  const outcome = await groupChatAction('star', { conversationId, messageId, starred: false });
  if (outcome.kind === 'ok') {
    setGroupStar(conversationId, messageId, false);
    return true;
  }
  showGroupNotice(messageActionErrorKey('star', outcome));
  return false;
}

/**
 * Delete for me: the row goes at once (and its star with it), then "Message deleted for you" with Undo, which
 * restores it (`restore: true`) and puts the row back. A refused or failed delete puts it back with its message.
 */
export async function deleteGroupMessageForMe(conversationId: string, messageId: string, wasStarred: boolean): Promise<void> {
  setGroupHidden(conversationId, messageId, true);
  if (wasStarred) setGroupStar(conversationId, messageId, false);
  const outcome = await groupChatAction('delete-for-me', { conversationId, messageId });
  if (outcome.kind === 'ok') {
    showGroupNotice('chat.group.message.deletedForYou', undefined, {
      action: { labelKey: 'chat.group.message.undo', run: () => void restoreGroupMessageForMe(conversationId, messageId) },
    });
    return;
  }
  setGroupHidden(conversationId, messageId, false);
  if (wasStarred) setGroupStar(conversationId, messageId, true);
  if (outcome.kind === 'refused' && handleLostAccess(conversationId, outcome.refusal)) return;
  showGroupNotice(messageActionErrorKey('deleteForMe', outcome));
}

async function restoreGroupMessageForMe(conversationId: string, messageId: string): Promise<void> {
  const outcome = await groupChatAction('delete-for-me', { conversationId, messageId, restore: true });
  if (outcome.kind === 'ok' && outcome.data.hidden !== true) {
    setGroupHidden(conversationId, messageId, false);
    // The row comes back from the window: re-read it (a reload since the delete no longer holds it). The star
    // the delete dropped stays dropped, as on the server.
    requestGroupReload(conversationId);
    return;
  }
  if (outcome.kind === 'refused' && handleLostAccess(conversationId, outcome.refusal)) return;
  showGroupNotice(messageActionErrorKey('deleteForMe', outcome.kind === 'ok' ? { kind: 'failed', status: 200 } : outcome));
}

/** Forward to 1..5 groups under ONE clientRequestId per sheet. The toast on success; the sheet's error otherwise. */
export async function forwardGroupMessage(conversationId: string, messageId: string, targetConversationIds: string[], clientRequestId: string): Promise<ForwardSummary> {
  const outcome = await groupChatAction('forward', { conversationId, messageId, targetConversationIds, clientRequestId });
  if (outcome.kind === 'refused' && handleLostAccess(conversationId, outcome.refusal)) return { toast: null, filesKey: null, errorKey: null };
  const summary = forwardSummary(outcome);
  if (summary.toast) showGroupNotice(summary.toast.key, summary.toast.vars, summary.filesKey ? { extraKey: summary.filesKey } : undefined);
  return summary;
}

/** Copy a row's text, with the "Copied" toast. */
export async function copyGroupMessage(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    showGroupNotice('chat.group.message.copied');
  } catch {
    // The browser refused the clipboard: nothing was copied, so nothing is announced.
  }
}
